import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./index.css";
import { post, get } from "../../services/ApiService.js";
import { useParams } from "react-router-dom";
import SkeletonLoader from "./screens/SkeletonLoader.jsx";
import NotFound from "./screens/NotFound.jsx";
import NoQuestions from "./screens/NoQuestions.jsx";
import Thankyou from "./screens/Thankyou.jsx";
import Splash from "./screens/Splash.jsx";
import UserInfo from "./screens/UserInfo.jsx";
import BmiCalculator from "./screens/BmiCalculator.jsx";
import QuestionForm from "./screens/QuestionForm.jsx";
import Header from "./screens/Header.jsx";
import { bmiData } from "../../redux/slices/BmiSlice.js";
import Products from "./screens/Products.jsx";

const GeneralConsultation = () => {
  const bmiState = useSelector(bmiData);
  const { id } = useParams();
  const [formData, setFormData] = useState({
    answers: [],
    explanations: [],
    userInfo: {
      name: "",
      email: "",
      phone: "",
      dob: "",
      address1: "",
      zip: "",
    },
  });
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [userInfoErrors, setUserInfoErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isBmiRequire, setIsBmiRequire] = useState(false);
  const [isProductsEnable, setIsProductsEnable] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      const res = await get(`api/forms/consultation/${id}`);

      if (res.success) {
        setForm(res.form);
        setQuestions(res.form.questions);
        setIsBmiRequire(res.form.requiresBMI);
      } else {
        setApiError(res.message || "Failed to load form");
      }
    } catch (error) {
      setApiError(
        "An error occurred while loading the form. Please try again."
      );
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentQuestion = () => {
    const questionStep = form.requiresBMI ? currentStep - 2 : currentStep - 1;
    // console.log("Current Step:", currentStep, "Question Step:", questionStep);
    return questions.find((q) => q.step === questionStep);
  };

  const handleUserInfoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      userInfo: {
        ...prev.userInfo,
        [field]: value,
      },
    }));

    if (userInfoErrors[field]) {
      setUserInfoErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateUserInfo = () => {
    const newErrors = {};
    if (!formData.userInfo.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.userInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.userInfo.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.userInfo.dob.trim()) {
      newErrors.dob = "Date of birth is required";
    }

    setUserInfoErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartAssessment = () => {
    if (validateUserInfo()) {
      if (isBmiRequire) {
        setCurrentStep(2); // Show BMI step
      } else {
        setCurrentStep(3); // Skip to questions
      }
    }
  };

  const handleBmiComplete = (bmiData) => {
    setIsBmiRequire(false);
    setCurrentStep(3);
  };

  const handleAnswer = (questionId, optionId, text, file) => {
    const question = questions.find((q) => q._id === questionId);
    if (!question) return;

    const currentIndex = questions.findIndex((q) => q._id === questionId);

    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[currentIndex];
      return updated;
    });

    const newAnswers = [...formData.answers];
    const currentAnswer = newAnswers[currentIndex] || {
      _id: question._id,
      options: [],
      description: "",
    };

    if (question.inputType === "radio" || question.inputType === "select") {
      currentAnswer.options = [optionId];
    } else if (question.inputType === "checkbox") {
      const currentOptions = currentAnswer.options || [];
      const isExclusive = question.options.find(
        (opt) => opt._id === optionId
      )?.exclusive;

      if (isExclusive) {
        currentAnswer.options = [optionId];
      } else {
        const filteredOptions = currentOptions.filter(
          (opt) => !question.options.find((o) => o._id === opt)?.exclusive
        );

        if (currentOptions.includes(optionId)) {
          currentAnswer.options = filteredOptions.filter(
            (opt) => opt !== optionId
          );
        } else {
          currentAnswer.options = [...filteredOptions, optionId];
        }
      }
    } else if (question.inputType === "text") {
      currentAnswer.description = text;
    } else if (question.inputType === "file") {
      currentAnswer.file = file;
    }

    newAnswers[currentIndex] = currentAnswer;

    if (question.inputType !== "text") {
      const requiresExplanation = currentAnswer.options?.some(
        (optId) =>
          question.options.find((o) => o._id === optId)?.requireExplanation
      );

      setFormData((prev) => {
        const newExplanations = [...prev.explanations];

        if (requiresExplanation) {
          const existingIndex = newExplanations.findIndex(
            (ex) => ex._id === question._id
          );

          if (existingIndex === -1) {
            newExplanations.push({ _id: question._id, text: "" });
          }
        } else {
          return {
            ...prev,
            answers: newAnswers,
            explanations: newExplanations.filter(
              (ex) => ex._id !== question._id
            ),
          };
        }

        return {
          ...prev,
          answers: newAnswers,
          explanations: newExplanations,
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        answers: newAnswers,
      }));
    }
  };

  const handleExplanationChange = (questionId, text) => {
    const currentQuestion = questions.find((q) => q._id === questionId);

    // Find the option that requires explanation from the current question
    const explanationOption = currentQuestion?.options?.find(
      (opt) => opt.requireExplanation
    );

    const optionId = explanationOption?._id || null;
    setFormData((prev) => {
      const newExplanations = [...prev.explanations];
      const existingIndex = newExplanations.findIndex(
        (ex) => ex._id === questionId
      );

      if (existingIndex >= 0) {
        newExplanations[existingIndex] = {
          ...newExplanations[existingIndex],
          text,
          optionId,
        };
      } else {
        newExplanations.push({ _id: questionId, text, optionId });
      }

      return {
        ...prev,
        explanations: newExplanations,
      };
    });
  };

  const isCurrentQuestionValid = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;

    const currentIndex = questions.findIndex(
      (q) => q._id === currentQuestion._id
    );
    const currentAnswer = formData.answers[currentIndex] || {
      options: [],
      description: "",
      file: "",
    };

    let isAnswered = false;
    if (currentQuestion.inputType === "text") {
      isAnswered = currentAnswer.description?.trim() !== "";
    } else if (currentQuestion.inputType === "file") {
      isAnswered = currentAnswer.file?.trim() !== "";
    } else {
      isAnswered = currentAnswer.options?.length > 0;
    }

    if (!isAnswered) return false;

    const requiresExplanation = currentQuestion.options?.some(
      (opt) =>
        currentAnswer.options?.includes(opt._id) && opt.requireExplanation
    );

    if (requiresExplanation) {
      const explanation = formData.explanations.find(
        (ex) => ex._id === currentQuestion._id
      );
      if (!explanation || !explanation.text?.trim()) return false;
    }

    return true;
  };

  // const handleNext = () => {
  //   if (isCurrentQuestionValid()) {

  //     const nextStep = currentStep + 1;
  //     const maxStep = form.requiresBMI
  //       ? questions.length + 2
  //       : questions.length + 1;
      
  //     if (nextStep <= maxStep) {
  //       setCurrentStep(nextStep);

  //       if (nextStep === maxStep) {
  //         setIsProductsEnable(true);
  //       }
  //     }
  //   } else {
  //     console.log(
  //       "Current question is not valid, cannot proceed to next step."
  //     );
  //   }
  // };

  const handleNext = () => {
    
  if (isCurrentQuestionValid()) {
    const nextStep = currentStep + 1;
    
    // Calculate the maximum step for questions only
    const maxQuestionStep = form.requiresBMI 
      ? questions.length + 2  // BMI + questions
      : questions.length + 1; // questions only
    
    // Check if there are more questions
    const currentQuestionIndex = questions.findIndex(
      (q) => q._id === getCurrentQuestion()?._id
    );
    const hasMoreQuestions = currentQuestionIndex < questions.length - 1;

    if (hasMoreQuestions) {
      setCurrentStep(nextStep);
    } else {
      setIsProductsEnable(true);
    }
  } else {
    console.log(
      "Current question is not valid, cannot proceed to next step."
    );
  }
};

  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isCurrentQuestionValid()) {
      const payload = {
        userInfo: formData.userInfo,
        answers: formData.answers,
        explanations: formData.explanations,
        formId: form._id,
        product: selectedProduct
      };
      if (bmiState) {
        payload.bmi = {
          score: bmiState?.bmiValue,
          category: bmiState?.bmiCategory,
          height: bmiState?.heightCm,
          weight: bmiState?.weightKg,
        };
      }
      try {
        setIsSubmit(true);
        const res = await post("api/forms/consultation/submit", payload);
        setIsSubmit(false);
        if (res.success) {
          setIsProductsEnable(false);
          setIsSubmitted(true);
        } else {
          setApiError(res.message || "Submission failed. Please try again.");
        }
      } catch (error) {
        setIsSubmit(false);
        setApiError("An error occurred during submission. Please try again.");
        console.error(error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="transcribed-consultation-form">
        <Header />
        <SkeletonLoader />
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="transcribed-consultation-form">
        <Header />
        <NotFound error={apiError} onRetry={fetchData} />
      </div>
    );
  }

  if (questions.length === 0 && !isLoading) {
    return (
      <div className="transcribed-consultation-form">
        <Header />
        <NoQuestions />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="transcribed-consultation-form">
        <Header />
        <Thankyou />
      </div>
    );
  }

  return (
    <div className="main_wrap_form main_wrap_form_top">
      <div className="transcribed-consultation-form">
        <Header />
        <div className="mt-0">
          {!isProductsEnable ? (
          <div className="row justify-content-md-center g-3">
            <div className="col-md-8 col-lg-6 col-12 container-border">
              <div className="w-form fade-in">
                <div className="card shadow-none border-0 rounded-4">
                  <div className=""></div>
                  {currentStep === 0 && (
                    <Splash form={form} onStart={() => setCurrentStep(1)} />
                  )}
                  {currentStep === 1 && (
                    <UserInfo
                      userInfo={formData.userInfo}
                      errors={userInfoErrors}
                      onChange={handleUserInfoChange}
                      onBack={() => setCurrentStep(0)}
                      onSubmit={handleStartAssessment}
                    />
                  )}
                  {currentStep === 2 && (
                    <BmiCalculator
                      onComplete={handleBmiComplete}
                      onBack={() => setCurrentStep(1)}
                    />
                  )}
                  {currentStep >= 3  && (
                    <QuestionForm
                      form={form}
                      questions={questions}
                      currentQuestion={getCurrentQuestion()}
                      currentIndex={questions.findIndex(
                        (q) => q._id === getCurrentQuestion()?._id
                      )}
                      currentAnswer={
                        formData.answers[
                          questions.findIndex(
                            (q) => q._id === getCurrentQuestion()?._id
                          )
                        ] || { options: [], description: "" }
                      }
                      requiresExplanation={getCurrentQuestion()?.options?.some(
                        (opt) =>
                          formData.answers[
                            questions.findIndex(
                              (q) => q._id === getCurrentQuestion()?._id
                            )
                          ]?.options?.includes(opt._id) &&
                          opt.requireExplanation
                      )}
                      descriptionLabel={
                        getCurrentQuestion()?.options?.find(
                          (opt) =>
                            formData.answers[
                              questions.findIndex(
                                (q) => q._id === getCurrentQuestion()?._id
                              )
                            ]?.options?.includes(opt._id) &&
                            opt.requireExplanation
                        )?.descriptionLabel || "Please provide more"
                      }
                      currentExplanation={
                        formData.explanations.find(
                          (ex) => ex._id === getCurrentQuestion()?._id
                        ) || { text: "" }
                      }
                      apiError={apiError}
                      isCurrentQuestionValid={isCurrentQuestionValid()}
                      isSubmit={isSubmit}
                      onAnswer={handleAnswer}
                      onExplanationChange={handleExplanationChange}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                      // onSubmit={handleSubmit}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          ):(
            <Products 
              setFinalPro={setSelectedProduct}
              onSubmit = {handleSubmit}
              isSubmit = {isSubmit}  
            />
          )}

        </div>
      </div>
    </div>
  );
};

export { GeneralConsultation };
